import { BcmOrder } from '@shared/models/bcm-order';
import { Quote } from '@shared/models/quote';
import { HttpParams } from '@angular/common/http';
import { ORDER_LIST_CLOSED_STATUS_TYPES } from '@shared/helpers/order-overview-model.util';
import { formatBoatOrderPhotoOptionLabel } from '@modules/bcm/boats/boat/documents/boat-order-photo-order-option.util';

export interface QuoteCommissionOrderOption {
    orderId: number;
    label: string;
    matchesQuoteRecipient: boolean;
}

export const QUOTE_COMMISSION_ORDER_PICKER_PAGE_SIZE = 50;

export function buildQuoteCommissionOrderPickerHttpParams(options: {
    searchTerm?: string;
    page?: number;
    size?: number;
} = {}): HttpParams {
    let params = new HttpParams()
        .set('page', String(options.page ?? 0))
        .set('size', String(options.size ?? QUOTE_COMMISSION_ORDER_PICKER_PAGE_SIZE))
        .set('orderBy', 'startTime')
        .set('orderByOrder', 'desc');

    const searchTerm = (options.searchTerm || '').trim();

    if (searchTerm) {
        params = params.set('filter', searchTerm);
    }

    return params;
}

export function resolveQuoteCommissionInitialOrderSearchTerm(
    quote: Pick<Quote, 'recipientName' | 'company' | 'person'>,
): string {
    const recipientName = (quote.recipientName || '').trim();

    if (recipientName) {
        return recipientName;
    }

    const companyName = (quote.company?.name || '').trim();

    if (companyName) {
        return companyName;
    }

    const personName = [
        quote.person?.firstName,
        quote.person?.lastName,
    ]
        .filter(Boolean)
        .join(' ')
        .trim();

    return personName;
}

export function formatQuoteCommissionOrderOptionLabel(
    order: Pick<BcmOrder, 'id' | 'orderNumber' | 'orderTitle' | 'clientName'>,
): string {
    return formatBoatOrderPhotoOptionLabel(order);
}

export function isOrderEligibleForQuoteCommission(order: BcmOrder): boolean {
    return !ORDER_LIST_CLOSED_STATUS_TYPES.includes(order.lastStatus);
}

export function orderMatchesQuoteRecipient(
    order: BcmOrder,
    quote: Pick<Quote, 'bcmPersonsId' | 'bcmCompaniesId'>,
): boolean {
    if (quote.bcmCompaniesId && order.clientCompany?.id === quote.bcmCompaniesId) {
        return true;
    }

    if (quote.bcmPersonsId && order.client?.id === quote.bcmPersonsId) {
        return true;
    }

    return false;
}

export function matchesQuoteCommissionOrderSearch(order: BcmOrder, searchTerm: string): boolean {
    const normalized = (searchTerm || '').trim().toLowerCase();

    if (!normalized) {
        return true;
    }

    const haystack = [
        formatQuoteCommissionOrderOptionLabel(order),
        String(order.id ?? ''),
        order.orderTitle,
        order.clientName,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

    return normalized
        .split(/\s+/)
        .filter(Boolean)
        .every((token) => haystack.includes(token));
}

function compareQuoteCommissionOrders(
    left: BcmOrder,
    right: BcmOrder,
    quote: Pick<Quote, 'bcmPersonsId' | 'bcmCompaniesId'>,
): number {
    const leftRecipientMatch = orderMatchesQuoteRecipient(left, quote) ? 1 : 0;
    const rightRecipientMatch = orderMatchesQuoteRecipient(right, quote) ? 1 : 0;

    if (leftRecipientMatch !== rightRecipientMatch) {
        return rightRecipientMatch - leftRecipientMatch;
    }

    return (right.id ?? 0) - (left.id ?? 0);
}

export function buildQuoteCommissionOrderOptions(
    orders: readonly BcmOrder[],
    quote: Pick<Quote, 'bcmPersonsId' | 'bcmCompaniesId'>,
    searchTerm: string,
    applyClientSearch = true,
): QuoteCommissionOrderOption[] {
    return [...orders]
        .filter(isOrderEligibleForQuoteCommission)
        .filter((order) => !applyClientSearch || matchesQuoteCommissionOrderSearch(order, searchTerm))
        .sort((left, right) => compareQuoteCommissionOrders(left, right, quote))
        .map((order) => ({
            orderId: order.id,
            label: formatQuoteCommissionOrderOptionLabel(order),
            matchesQuoteRecipient: orderMatchesQuoteRecipient(order, quote),
        }));
}
